import { useEffect, useRef, useState} from "react";
import Matter from "matter-js";
import './spoondrop.css';
import { swapDocBody, useGameState, MAX_HEIGHT, MAX_WIDTH, drawHUD, getDroppingIndicator, getSpoon, resizeDroppingIndicator } from "./util/spoonHelper";
import { createMatterEngine } from "./util/createMatterEngine";
import { GameShell } from "./util/GameShell";
import { createPausableTimerGroup } from "./util/pausableTimers";

const SpoonDropHotSpoontato = () => {
  const gameKey = "hotSpoontato";
  const flavor = "AH ouch! The spoons are hot hot hot." ;
  const instructions = "Click or tap and drag to move the trampoline and bounce the spoons until they're cool";
  const hudRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const gameState = useGameState(flavor, instructions, gameKey);
  const { canvasRef, canvasHeight, setCanvasHeight, restartRef, setPlayButtonText,
          setGameOverState, setMessage, setScoreText, recordScore,
          gameStartedRef, rebuildKey, pauseRef, resumeRef,} = gameState;
  useEffect(() => swapDocBody(), []);
  useEffect(() => {
    const {
      Bodies,
      Body,
      Composite,
      Composites,
      Constraint,
      Mouse,
      MouseConstraint,
      Vector,
      Events,
    } = Matter;

    const allSpoons = [];
    const spoonsInQueue = [];
    const indicators = [];
    var isMobile = false;
    
    const width = Math.min(window.innerWidth, MAX_WIDTH);
    const height = Math.min(window.innerHeight, MAX_HEIGHT);
    setCanvasHeight(height);
    setCanvasWidth(width);
    
    const timers = createPausableTimerGroup();
    const { engine, runner, render, start, cleanup } = createMatterEngine(timers, canvasRef, width, height);

    var margin = width/10;
    var fric = 0.03;
    engine.gravity.y = 0.3;
    var restitutionValue = 0.8;
    var size = 100; // size var for spoon
    let lives = 3;
    // Trampoline
    var segmentCount = 11;
    var segmentWidth = 40;
    var segmentHeight = 30;
    if(width < 900){
      isMobile = true;
      size = width > 500 && height > 500 ? 75 : 50;
      fric = 0.15;
      segmentCount = 5;
      segmentWidth = 28;
      segmentHeight = 15;
      margin = width/10;
    } 
    var gameWidth = width-2*margin;
    const startX = width / 2 - (segmentCount * segmentWidth) / 2;
    const startY = height * 0.8;

    const trampoline = Composites.stack(
      startX,
      startY,
      segmentCount,
      1,
      0,
      0,
      (x, y) =>
        Bodies.rectangle(x, y, segmentWidth, segmentHeight, {
          restitution: restitutionValue,
          frictionAir: fric,
          friction: fric,
          collisionFilter: {
            group:2,
            category: 2, 
            mask: 4
          },
          render: { fillStyle: "#eabf04" },
          chamfer: { radius: 6 },
        })
    );

    Composites.chain(trampoline, 0.5, 0, -0.5, 0, {
      stiffness: 1,
      length: 2,
      render: { visible: false },
    });

    // Rails at ends (invisible, kinematic)
    const leftRail = Bodies.rectangle(startX, startY, 10, 10, {
      isStatic: false,
      isSensor: true,
      collisionFilter: { group: -1 },
      render: { visible: false },
    });
    Body.setVelocity(leftRail, { x: 0, y: 0 });
    leftRail.isSleeping = true;

    const rightRail = Bodies.rectangle(
      startX + segmentCount * segmentWidth,
      startY,
      10,
      10,
      {
        isStatic: false,
        isSensor: true,
        collisionFilter: { group: -1 },
        render: { visible: false },
      }
    );
    Body.setVelocity(rightRail, { x: 0, y: 0 });
    rightRail.isSleeping = true;

    Composite.add(engine.world, [leftRail, rightRail]);

    const leftConstraint = Constraint.create({
      bodyA: trampoline.bodies[0],
      pointA: { x: -segmentWidth / 2, y: 0 },
      bodyB: leftRail,
      pointB: { x: 0, y: 0 },
      length: 0,
      stiffness: 1,
      render: { visible: false },
    });

    const rightConstraint = Constraint.create({
      bodyA: trampoline.bodies[segmentCount - 1],
      pointA: { x: segmentWidth / 2, y: 0 },
      bodyB: rightRail,
      pointB: { x: 0, y: 0 },
      length: 0,
      stiffness: 1,
      render: { visible: false },
    });

    Composite.add(engine.world, [leftConstraint, rightConstraint]);
    Composite.add(engine.world, trampoline);

    // Mouse and dragging trampoline horizontally with damping
    const mouse = Mouse.create(render.canvas);
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false },
      },
    });
    Composite.add(engine.world, mouseConstraint);

    // Variables for smooth movement
    let isDragging = false;

    Events.on(mouseConstraint, "mousedown", () => {
      isDragging = true;
    });

    Events.on(mouseConstraint, "mouseup", () => {
      isDragging = false;
    });
    
    Events.on(engine, "beforeUpdate", () => {
      if (isDragging) {
        let totalSpoonMass = 0;
    
        allSpoons.forEach(({ body }) => {
          if (!body.isSleeping) {
            totalSpoonMass += body.mass;
          }
        });
    
        const maxMass = 50;
        const weightFactor = Math.min(totalSpoonMass / maxMass, 1);
        const dampingBase = 0.2;
        const boost = weightFactor * 0.5;
        const damping = dampingBase + boost;
    
        const targetX = mouse.position.x;
        const currentCenterX = (leftRail.position.x + rightRail.position.x) / 2;
    
        const newCenterX = currentCenterX + (targetX - currentCenterX) * damping;
        const halfWidth = (segmentCount * segmentWidth) / 2;
    
        const newLeftX = newCenterX - halfWidth;
        const newRightX = newCenterX + halfWidth;
    
        // Always lock rails Y position and Y velocity
        Body.setPosition(leftRail, Vector.create(newLeftX, startY));
        Body.setVelocity(leftRail, Vector.create(0, 0));
    
        Body.setPosition(rightRail, Vector.create(newRightX, startY));
        Body.setVelocity(rightRail, Vector.create(0, 0));
      } else {
        // When not dragging, allow X drift but lock Y for rails
        Body.setPosition(leftRail, Vector.create(leftRail.position.x, startY));
        Body.setVelocity(leftRail, Vector.create(leftRail.velocity.x, 0));
    
        Body.setPosition(rightRail, Vector.create(rightRail.position.x, startY));
        Body.setVelocity(rightRail, Vector.create(rightRail.velocity.x, 0));
      }
    
      // Lock left and right trampoline ends to rails in Y (unless netBump)
      const leftSegment = trampoline.bodies[0];
      const rightSegment = trampoline.bodies[trampoline.bodies.length - 1];
    
      Body.setPosition(leftSegment, Vector.create(leftSegment.position.x, netBump ? leftSegment.position.y : startY));
      Body.setVelocity(leftSegment, Vector.create(leftSegment.velocity.x, netBump ? leftSegment.velocity.y : 0));
    
      Body.setPosition(rightSegment, Vector.create(rightSegment.position.x, netBump ? rightSegment.position.y : startY));
      Body.setVelocity(rightSegment, Vector.create(rightSegment.velocity.x, netBump ? rightSegment.velocity.y : 0));

      allSpoons.forEach(spoon => {
        if(spoon.body.beenOnScreen && spoon.body.position.y <= -size){
          let triangle = getDroppingIndicator(spoon.body.position.x, spoon.body.color, isMobile);
          resizeDroppingIndicator(triangle, spoon.body.position.y);
          Composite.add(engine.world, triangle)
          timers.setTimeout(()=>{
            Composite.remove(engine.world, triangle);
          }, 10)
        }
      })

    });

    //----------BUMPERS------------
    var bumperWidth = 50;
    const bumperHeight = height * 5; // taller than canvas
    const bumperY = height / 2;
    if (isMobile){
      bumperWidth = 20;
    }

    const leftBumper = Bodies.rectangle(
      0,
      bumperY,
      bumperWidth,
      bumperHeight,
      {
        isStatic: true,
        label: "bumper",
        isSensor: false, // so it doesn’t block, just triggers
        collisionFilter: {
          category: 8, // unique category
          mask: 4 // only spoons (category 4) can collide
        },
        render: { visible: true },
      }
    );

    const rightBumper = Bodies.rectangle(
      width,
      bumperY,
      bumperWidth,
      bumperHeight,
      {
        isStatic: true,
        label: "bumper",
        isSensor: false,
        collisionFilter: {
          category: 8,
          mask: 4
        },
        render: { visible: true },
      }
    );

    leftBumper.isBumper = true;
    rightBumper.isBumper = true;
    Composite.add(engine.world, [leftBumper, rightBumper]);

    
    
    let bumpNeeded = false;

    // Game state vars
    var dropSpoons;
    var points = 0;
    var doBumps;
    var tracker = 0;
    var initialSpeed = 250;
    var speed = initialSpeed;


    const ongoingPushes = new Map(); // Map<spoon.id, intervalId>

    let netBump = false; // 
    // Collision handling for bouncing spoons off trampoline segments
    Events.on(engine, "collisionStart", function (event) {
      var bumperPowerY = -0.015;
      var bumperPowerX = 0.00005;
      var maxTicks = 4;
      if (isMobile) {
        bumperPowerY = -0.005;
        bumperPowerX = 0.0001;
        maxTicks = 2;
      }
      event.pairs.forEach((pair) => {
        const { bodyA, bodyB } = pair;

        let trampolineBody = null;
        let targetBody = null;
        // console.log("collision");
        if (!(trampoline.bodies.includes(bodyB) && trampoline.bodies.includes(bodyA))) {
          //collision logic for trampoline bumping spoons
          if (trampoline.bodies.includes(bodyA)) {
            trampolineBody = bodyA;
            targetBody = bodyB;
          } else if (trampoline.bodies.includes(bodyB)) {
            trampolineBody = bodyB;
            targetBody = bodyA;
          }
          if (trampolineBody && targetBody) {
            targetBody.parent.beenOnScreen = true;
            bumpNeeded = true;
            netBump = true;
            timers.setTimeout(() => { Matter.Body.applyForce(targetBody.parent, targetBody.position, { x: 0, y:  bumperPowerY/2})
            netBump = false; }, 1000);
            timers.setTimeout(() => { Matter.Body.applyForce(targetBody.parent, targetBody.position, { x: 0, y:  bumperPowerY});
            }, 100);
          }
        }

        //collision logic for spoon and bumper
        const bumper = [bodyA, bodyB].find(b => b.label === "bumper");
        const spoonPart = [bodyA, bodyB].find(b => b.label !== "bumper");
        if (bumper && spoonPart?.isSpoonPart) {
          const spoon = spoonPart.parentSpoon;
          if (spoon && spoon.parts) {
            if (ongoingPushes.has(spoon.id)) {
              // Already pushing this spoon, don't start again
              return;
            }
            bumper.render.fillStyle = "#39ad5e";
            timers.setTimeout(() => {
              bumper.render.fillStyle = "transparent";
            }, 100);
        
            const centerX = window.innerWidth / 2;
            const dx = centerX - spoon.position.x;
            const xDir = dx * bumperPowerX; // smaller per tick
            const yDir = bumperPowerY;
        
            let ticks = 0;
        
            const intervalId = timers.setInterval(() => {
              if (ticks >= maxTicks) {
                timers.clearInterval(intervalId);
                ongoingPushes.delete(spoon.id);
                return;
              }
              ticks++;
        
              spoon.parts.forEach(part => {
                Matter.Sleeping.set(part, false);
                Matter.Body.applyForce(part, part.position, { x: xDir, y: yDir });
              });
            }, 16); // roughly one frame at 60fps
        
            ongoingPushes.set(spoon.id, intervalId);
          }
        }
        

      });
    });

    let bumpTime = 0;
    var trampForce = -0.01;
    if (isMobile) {
      trampForce = -0.005;
    }
    Events.on(engine, "afterUpdate", () => {
      if (bumpNeeded) {
        bumpTime = 0;
        doBumps = timers.setInterval(() => {
          if(bumpTime < 2) {
            trampoline.bodies.forEach(segment => {
              Body.applyForce(segment, segment.position, { x: 0, y: trampForce }); // stronger upward force
            });
            bumpTime++;
          }
          else {
            timers.clearInterval(doBumps);
          }
        }, 50);
        bumpNeeded = false; // reset so it only fires once
      }
    });

    // Spawn falling spoons function
    function getRandomInt(max) {
      return Math.floor(Math.random() * max);
    }
    function clearAllQueuedSpoons(){
      spoonsInQueue.forEach(interval => {
        timers.clearInterval(interval)
      })
      spoonsInQueue.splice(0, spoonsInQueue.length);
      indicators.forEach(body => {
        Composite.remove(engine.world, body);
      })
      indicators.splice(0, indicators.length);
      allSpoons.forEach(body => {
        Composite.remove(engine.world, body);
      })
      allSpoons.splice(0, allSpoons.length);
      
    }
    function spawnFallingSpoon() {
      let obstacleSize = 5*(size * 0.225 + size * Math.random() * 0.05); //slight variation to spawn size
      let xposSpawn = gameWidth * Math.random() + margin; 
      let indicatorShowing = false; 
      let indicatorTimeTracker = 10;
      let color = spoonColors[0];
      let triangleBody = getDroppingIndicator(xposSpawn, color, isMobile);
      indicators.push(triangleBody);
      let indicatorBlinkingInterval = timers.setInterval(() => {
        if(indicatorShowing){
          Composite.remove(engine.world, triangleBody)
        }
        else{
          Composite.add(engine.world, triangleBody)
        }
        indicatorShowing = !indicatorShowing;
        indicatorTimeTracker--;
        if(indicatorTimeTracker<=0){
          let spoonObstacle = getFallingSpoon(obstacleSize, xposSpawn);
          spoonObstacle.color = color;
          allSpoons.push({body: spoonObstacle, temp: startingHeat, isCooled: false});
          spoonHeatValues.push(startingHeat);
          Composite.add(engine.world, spoonObstacle);
          timers.clearInterval(indicatorBlinkingInterval);
          spoonsInQueue.shift();
          indicators.shift();
        }
      }, 500);
      spoonsInQueue.push(indicatorBlinkingInterval);
    }

    //slight variation on density of spoon based on color
    const spoonColors = ["#bf3317", "#db8c2c","#e6ca40", "#54a8a7"];
    const spoonGrav = [0.008, 0.0006, 0.007];

    //returns a spoon to fall from top of screen
    //TODO Refactor this 
    function getFallingSpoon(obstacleSize, xposSpawn){
      let spoonType = getRandomInt(3);
      let spoonDensity = spoonGrav[spoonType];
      let collisionFilter = {
          group: -2,
          category: 4,
          mask: 2 | 8,
        }
      let spoon = getSpoon(obstacleSize, xposSpawn, -100, collisionFilter, spoonColors[0], "middle", .6);
      spoon.parts.forEach(part => {
        part.restitution = restitutionValue;
        part.density = spoonDensity;
        part.label = "spoon";
        part.isSpoonPart = true;
        part.parentSpoon = spoon; // link to parent
      });
      spoon.color = spoonColors[0];
      spoon.beenOnScreen = false;
      
      //ANGLE
      let angle = Math.random()*2 >=1 ? Math.PI/2 : -Math.PI/2;
          
      Body.setAngle(spoon, angle);

      if (points > 6 && Math.random() * 2 < 1 ) {//some of em spin :)
        Body.setAngularVelocity(
          spoon,
          ((obstacleSize - Math.random() * obstacleSize) / 600) * (getRandomInt(3) - 1)
        );
      }

      return spoon;
    }

    function createPlusOne(x, y) {
      const parts = [];
    
      // "+" sign made of two rectangles
      const plusHorizontal = Matter.Bodies.rectangle(x, y, 20, 5, {
        isStatic: true,
        render: {
          fillStyle: "#ffffff"
        }
      });
    
      const plusVertical = Matter.Bodies.rectangle(x, y, 5, 20, {
        isStatic: true,
        render: {
          fillStyle: "#ffffff"
        }
      });
    
      const one = Matter.Bodies.rectangle(x + 15, y, 5, 25, {
        isStatic: true,
        render: {
          fillStyle: "#ffffff"
        }
      });
    
      parts.push(plusHorizontal, plusVertical, one);
    
      const plusOneComposite = Matter.Body.create({
        parts,
        isStatic: true,
        collisionFilter: {
          mask: 0, // No collisions
        }
      });
    
      // Optional: animate upward float and fade
      let opacity = 1;
      const floatInterval = timers.setInterval(() => {
        // Move upward slightly
        Matter.Body.translate(plusOneComposite, { x: 0, y: -1 });
    
        // Fade out by reducing opacity
        opacity -= 0.05;
        plusOneComposite.parts.forEach(part => {
          part.render.opacity = opacity;
        });
    
        if (opacity <= 0) {
          timers.clearInterval(floatInterval);
          Matter.Composite.remove(engine.world, plusOneComposite);
        }
      }, 50);
    
      Matter.Composite.add(engine.world, plusOneComposite);
    }
    

    function handleCooledSpoon(spoon) {
      let spoonBody = spoon.body;
      createPlusOne(spoonBody.position.x, spoonBody.position.y - 50);
      if (!spoon.isCooled) {
        spoonBody.parts.forEach(part => {
          part.render.fillStyle = spoonColors[3];
          part.collisionFilter = {
            group: -2,
            category: 4,
            mask: 0,
          }
        });
        spoonBody.color = spoonColors[3]
        //increase points once per spoon
        points++;
        spoon.isCooled = true;
      }

    }

    function changeColor(spoon, color) {
      spoon.parts.forEach(part => {
        part.render.fillStyle = spoonColors[color];
      });
      spoon.color = spoonColors[color];
    }

    var spoonHeatValues = [];
    var startingHeat = 150;
    function spoonHeatDecay(){
      allSpoons.forEach((spoon) => {
        if (getRandomInt(3) <= 1) {
          // 2/3 of the time decay heat
          spoon.temp -= 1;
        }
        if (spoon.temp <= 0) {
          handleCooledSpoon(spoon);
        } else if (spoon.temp <= (1 / 3) * startingHeat) {
          changeColor(spoon.body, 2); // yellow
        } else if (spoon.temp <= (2 / 3) * startingHeat) {
          changeColor(spoon.body, 1); // orange
        }
      });
    }

    function detectDroppedSpoons(){
      let droppedSpoons = false;
      var spoonsToRemove = [];
      allSpoons.forEach((spoon, index) => {
        // console.log("spoon found")
        if (spoon.body.position.y > height + 100 ) {
          if (!spoon.isCooled) {
            droppedSpoons = true;
          }
          //cleanup spoons off screen
          spoonsToRemove.push(index);
          Composite.remove(engine.world, spoon);
        }
      });
      //clean up array of spoons
      for (let i = spoonsToRemove.length - 1; i >= 0; i--) {
        allSpoons.splice(spoonsToRemove[i], 1);
      }
      if(droppedSpoons && gameStarted){
        lives = Math.max(0, lives - 1);
        if(lives <= 0) {
          gameOver();
          //deal with spoons so they can fly up and then get deleted
         allSpoons.splice(0, allSpoons.length);
        }
      }
      //if there are no spoons change spawn one sooner
      if(allSpoons.length ===0 && droppedSpoons){
        tracker = 1.4*speed
      }
    }

    // Game control vars
    var gameStarted = false;
    var resettable = false;

    function restartGame() {
      if (resettable === true) {
        points = 0;
        setScoreText(points);            // reset score
        resettable = false;
        tracker = 0;
        lives = 3;
        speed = initialSpeed;

        // Reset trampoline rails to center
        Body.setPosition(leftRail, Vector.create(startX, startY));
        Body.setPosition(rightRail, Vector.create(startX + segmentCount * segmentWidth, startY));
        startGame();
      }
    }
    //TODO: add more messages
    function gameOver() {
      recordScore(points);
      setScoreText(points + " cool spoons");
      clearAllQueuedSpoons();
      let endMessage = getPopupMessage();
      setMessage(endMessage);
      gameStarted = false;
      gameStartedRef.current = false;
      resettable = true;
      timers.clearInterval(dropSpoons);
      trampoline.bodies.forEach(segment => {
        const netForce = { x: 0, y: 1 }; // throw out the spoons
        Body.applyForce(segment, segment.position, netForce);
      });
      const tutEl = document.getElementById("descenttut");
      if (tutEl) tutEl.innerHTML = "";
      const dropperEl = document.getElementById("dropper");
      if (dropperEl) dropperEl.innerHTML = "";
      
      timers.setTimeout(() => {
        setGameOverState(true); // Show game over screen
      }, 1100)
    }

    var debug = false;
    function startGame() {
      if (gameStarted === false) {
        setGameOverState(false); // Show game over screen
        gameStarted = true;
        gameStartedRef.current = true;
        const tutEl = document.getElementById("descenttut");
        if (tutEl) tutEl.innerHTML = "";

        dropSpoons = timers.setInterval(() => {
          if (document.getElementById("dropper") === null) {
            timers.clearInterval(dropSpoons);
            return;
          }
          spoonHeatDecay()
          detectDroppedSpoons()
          if(doSpawnCheck()){
            spawnFallingSpoon();
          }
          if(gameStarted){
            setText();
          }
        }, 100);
      }
    }
    //handles increasing speed of game spawns
    //returns true if spawn should occur
    function doSpawnCheck() {
      let doSpawn = false;
      //speed change for spoon drops
      //after 12 it's close to max speed
      if (tracker / (1.5 * speed) > 1 && speed > 200) {
        speed = speed - 50;
        tracker = 1;
        doSpawn = true;
      }
      //more gradual speed change to top speed
      else if (tracker / (1.5 * speed) > 1 && speed > 100) {
        speed-=25;
        tracker = 1;
        doSpawn = true;
      }
      if (tracker % speed === 0) {
        doSpawn = true;
      }
      tracker++;
      return doSpawn; 
    }

    function setText() {
      const dropperEl = document.getElementById("dropper");
      //point tracking messages
      if(points >= 20){
        if (dropperEl) dropperEl.innerHTML = "Whoa! " + points + " cool spoons";
      }
      else if(points >= 10){
        if (dropperEl) dropperEl.innerHTML = "Nice! " + points + " cool spoons"; 
      }
      else{
        if (dropperEl) dropperEl.innerHTML = points + " cool spoons";
      }
      if (dropperEl && debug) dropperEl.innerHTML = "Whoa! " + points + " cool spoons. Speed = " + speed;
    }

    function getPopupMessage(isStart){
      if(isStart){
        return "context!"
      }
      let message;
      if(points >= 20){
        message = "You are a professional cool guy/gal, aren't you?";
      }
      else message = "If you can't take the heat, get out of the utensil drawer. \n Wait, why are they hot?";
      return message;
    }
      
    function startRestart(){
      if(!gameStarted && !resettable){startGame()}
      else{
        restartGame();
      }
      setPlayButtonText("Restart")
    }
    restartRef.current = startRestart;
    pauseRef.current = () => {
      runner.enabled = false;
      timers.pauseAll();
      setScoreText("Score: " + points + " points")
    };
    resumeRef.current = () => {
      runner.enabled = true;
      timers.resumeAll();
    };

    start();
    setGameOverState(true); // Show game over screen
    
    drawHUD(() => lives, () => gameStarted, hudRef);
    

    // Cleanup on unmount
    return cleanup;
  }, [canvasRef, restartRef, setCanvasHeight, setGameOverState, setMessage, setPlayButtonText, setScoreText, recordScore, rebuildKey, gameStartedRef, pauseRef, resumeRef]);

  return (
    <GameShell gameName="Hot Spoontato" canvasRef={canvasRef} gameState={gameState}>
      <canvas ref={hudRef} className="hud" style={{ '--canvas-height': `${canvasHeight}px`, '--canvas-width': `${canvasWidth}px`, 
          position: "absolute", zIndex: 1, pointerEvents: "none" }} />
    </GameShell>
  );
};

export default SpoonDropHotSpoontato;
